import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Home, UserCog, LogOut, Activity, Swords, User, Shield, Trophy } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import FbmLogo from "@/components/FbmLogo";

const navItems = [
  { to: "/scout", label: "Home", icon: Home, end: true, testId: "scout-nav-home" },
  { to: "/scout/players", label: "Players", icon: User, testId: "scout-nav-players" },
  { to: "/scout/teams", label: "Teams", icon: Shield, testId: "scout-nav-teams" },
  { to: "/scout/leagues", label: "Leagues", icon: Trophy, testId: "scout-nav-leagues" },
  { to: "/scout/matches", label: "Matches", icon: Swords, testId: "scout-nav-matches" },
  { to: "/scout/account", label: "Account", icon: UserCog, testId: "scout-nav-account" },
];

export default function ScoutLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="scout-theme min-h-screen bg-background text-foreground flex">
      <aside className="scout-sidebar w-64 shrink-0 flex flex-col" data-testid="scout-sidebar">
        <div className="px-6 py-7 border-b border-border">
          <div className="flex items-center gap-3">
            <FbmLogo size={36} withText={false} theme="light" />
            <div>
              <div className="font-display uppercase tracking-[0.2em] text-[11px] text-muted-foreground">FBM</div>
              <div className="font-display uppercase tracking-tight text-lg">Scout</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-md font-display uppercase tracking-[0.14em] text-sm transition-colors ${
                    isActive
                      ? "bg-foreground text-background"
                      : "text-foreground/70 hover:text-foreground hover:bg-foreground/5"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-4 py-5 border-t border-border space-y-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Activity className="h-3.5 w-3.5" />
            <span className="uppercase tracking-[0.18em]">Scout</span>
          </div>
          <div className="text-sm truncate" data-testid="scout-user-name">{user?.full_name}</div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="w-full h-10 rounded-none font-display uppercase tracking-[0.18em] text-xs"
            data-testid="scout-logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 py-8 md:py-12">
          <Outlet />
        </div>
      </main>

      <Toaster richColors position="top-right" />
    </div>
  );
}
